import { useState } from "react";
import { Link } from "react-router-dom";
import { popularCategories } from "@/mocks/kakobuy-data";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    setStatus("loading");
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      await fetch("https://readdy.ai/api/form/d8ugnkpl2p03kijan2q0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <footer className="bg-foreground-900 text-background-200">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4 cursor-pointer">
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-lg font-semibold text-background-50">
                Kakobuy Spreadsheet
              </span>
            </Link>
            <p className="text-sm text-background-400 leading-relaxed mb-4">
              The most trusted Kakobuy Spreadsheet resource. Verified products, trusted sellers, and expert buying guides updated weekly.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-800 text-background-300 hover:text-background-50 hover:bg-primary-500 transition-all duration-300 cursor-pointer" aria-label="Twitter">
                <i className="ri-twitter-x-line text-sm"></i>
              </a>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-800 text-background-300 hover:text-background-50 hover:bg-primary-500 transition-all duration-300 cursor-pointer" aria-label="Instagram">
                <i className="ri-instagram-line text-sm"></i>
              </a>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-800 text-background-300 hover:text-background-50 hover:bg-primary-500 transition-all duration-300 cursor-pointer" aria-label="Reddit">
                <i className="ri-reddit-line text-sm"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              <li><a href="#categories" className="text-sm text-background-400 hover:text-background-100 transition-colors cursor-pointer">Categories</a></li>
              <li><a href="#trust" className="text-sm text-background-400 hover:text-background-100 transition-colors cursor-pointer">Why Trust Us</a></li>
              <li><a href="#guides" className="text-sm text-background-400 hover:text-background-100 transition-colors cursor-pointer">Buying Guides</a></li>
              <li><a href="#faq" className="text-sm text-background-400 hover:text-background-100 transition-colors cursor-pointer">FAQ</a></li>
              <li><Link to="/guide" className="text-sm text-background-400 hover:text-background-100 transition-colors cursor-pointer">Spreadsheet Guide</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">Popular Categories</h4>
            <ul className="space-y-2.5">
              {popularCategories.slice(0, 5).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-400 hover:text-background-100 transition-colors cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">Newsletter</h4>
            <p className="text-sm text-background-400 leading-relaxed mb-4">
              Get weekly updates on the latest Kakobuy finds and trusted sellers.
            </p>
            <form data-readdy-form="" onSubmit={handleSubmit} className="flex flex-col gap-2.5">
              <div className="honeypot-field">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-foreground-800 border border-foreground-700 text-background-100 text-sm placeholder:text-background-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <button
                type="submit"
                disabled={status === "loading" || status === "success"}
                className="w-full px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {status === "loading" ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : status === "success" ? (
                  <>
                    <i className="ri-check-line"></i>
                    Subscribed
                  </>
                ) : (
                  <>
                    Subscribe
                    <i className="ri-arrow-right-line text-sm"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-500">
            &copy; {new Date().getFullYear()} Kakobuy Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-xs text-background-500 hover:text-background-300 transition-colors cursor-pointer">Main Site</a>
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-xs text-background-500 hover:text-background-300 transition-colors cursor-pointer">Privacy</a>
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-xs text-background-500 hover:text-background-300 transition-colors cursor-pointer">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}